import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { categoriesApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, Empty, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import Confirm from '@/components/common/Confirm'
import Drawer from '@/components/common/Drawer'
import TamilField from '@/components/common/TamilField'
import BulkTranslate from '@/components/common/BulkTranslate'
import { PERMS, can, useAuth } from '@/store/auth'

const BLANK = { name: '', name_ta: '', min_order_qty: '' }

// Hidden, not removed. Flip any of these back to true to show it again; the
// bulk-translate drawer, the Tamil data and the slug are all still loaded.
const SHOW_BULK_TRANSLATE = false
const SHOW_TAMIL_COLUMN = false
const SHOW_IMPORT_KEY_COLUMN = false

/**
 * The product categories, as the Products filter uses them.
 *
 * A category with products in it cannot be deleted — the API refuses, and the
 * button is disabled with the count showing, so the answer to "why not" is on
 * screen rather than in an error.
 */
/** 'kg' for kilograms, the unit's own word otherwise; '' when unknown. */
const unitShort = (unit) =>
  !unit ? '' : unit.toUpperCase() === 'KILOGRAMS' ? 'kg' : unit.toLowerCase()

export default function Categories() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [form, setForm] = useState(null)      // { id?, name, slug? }
  const [confirming, setConfirming] = useState(null)
  const [picked, setPicked] = useState({})
  const [bulk, setBulk] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const editable = can(user, PERMS.productsWrite)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['category-management'],
    queryFn: categoriesApi.list,
  })

  const done = (message) => {
    queryClient.invalidateQueries({ queryKey: ['category-management'] })
    // The Products page filter reads its own list of names.
    queryClient.invalidateQueries({ queryKey: ['categories'] })
    queryClient.invalidateQueries({ queryKey: ['products'] })
    setForm(null)
    setError('')
    setNotice(message)
  }

  const save = useMutation({
    mutationFn: ({ id, ...payload }) =>
      id ? categoriesApi.update(id, payload) : categoriesApi.create(payload),
    onSuccess: (category) => done(`Saved "${category.name}".`),
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: categoriesApi.remove,
    onSuccess: () => done('Category deleted.'),
    onError: (e) => setError(errorMessage(e)),
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load categories" onRetry={refetch} />

  // 'Pending' means no approved Tamil yet.
  const pending = data.filter((c) => !c.name_ta)
  const chosen = data.filter((c) => picked[c.id])
  const selectAllPending = () =>
    setPicked(Object.fromEntries(pending.map((c) => [c.id, true])))

  return (
    <div className="categories-page">
      <PageHeader
        title="Categories"
        subtitle="How the catalog is grouped on the Products screen and in reports"
        actions={
          <>
            <Link to="/products" className="btn btn-sm">
              Back to products
            </Link>
            {SHOW_BULK_TRANSLATE && editable && pending.length > 0 && (
              <button type="button" className="btn btn-sm" onClick={selectAllPending}>
                Select all pending ({pending.length})
              </button>
            )}
            {SHOW_BULK_TRANSLATE && editable && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={chosen.length === 0}
                title={
                  chosen.length === 0
                    ? 'Tick the categories to translate, or use Select all pending'
                    : undefined
                }
                onClick={() => {
                  setNotice('')
                  setBulk(true)
                }}
              >
                Translate selected ({chosen.length})
              </button>
            )}
            {editable && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setNotice('')
                  setForm(BLANK)
                }}
              >
                Add category
              </button>
            )}
          </>
        }
      />

      <Alert tone="success" message={notice} onClose={() => setNotice('')} />
      {!form && (
        <Alert tone="danger" message={error} onClose={() => setError('')} />
      )}

      {/* The same form, the same handlers — moved out of the page flow so
          clicking Edit no longer pushes the table down. */}
      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        title={form?.id ? 'Edit category' : 'Add category'}
        subtitle={form?.id ? form.name : 'It starts with no products in it.'}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="category-form"
              className="btn btn-primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <form
          id="category-form"
          onSubmit={(e) => {
            e.preventDefault()
            const { min_order_qty, base_unit: _unit, unit_minimums, ...rest } = form
            // Blank or 0 clears it.
            save.mutate({
              ...rest,
              min_order_qty:
                min_order_qty === '' || Number(min_order_qty) === 0 ? null : String(min_order_qty),
              // Every unit is sent, so blanking one clears it. The weight
              // beside each is read-only here — it belongs to Unit weights.
              unit_minimums: (unit_minimums ?? []).map((u) => ({
                unit_code: u.unit_code,
                min_order_qty:
                  String(u.min_order_qty ?? '').trim() === '' || Number(u.min_order_qty) === 0
                    ? null
                    : String(u.min_order_qty),
              })),
            })
          }}
        >
          <label className="field">
            <span>Name *</span>
            <input
              value={form?.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="MS WIRE NAILS"
              required
            />
          </label>
          <TamilField
            value={form?.name_ta}
            onChange={(name_ta) => setForm((f) => ({ ...f, name_ta }))}
            source={form?.name}
            entityType="category"
            label="Name (Tamil)"
            hint="Optional — English is the name the catalog and the import run on."
          />
          <label className="field">
            <span>
              Minimum order quantity
              {form?.base_unit ? ` (${unitShort(form.base_unit)})` : ''}
            </span>
            <input
              type="number"
              min="0"
              step="0.001"
              inputMode="decimal"
              value={form?.min_order_qty ?? ''}
              onChange={(e) => setForm({ ...form, min_order_qty: e.target.value })}
              placeholder="No minimum"
            />
          </label>
          <p className="hint">
            Applies to every product in this category, in the unit they are priced in
            {form?.base_unit ? ` (${unitShort(form.base_unit)})` : ''}. Orders in bags, coils
            and the like are converted with the unit weights before they are compared. A
            shorter order is flagged for correction — never changed.
          </p>
          {/* One per unit this category has a weight for. Entered rather
              than worked out from the kilograms above: 25 kg over a 9 kg
              carton is 2.78 cartons, and a shop does not sell 0.78 of a
              carton. The kilograms each one comes to is printed beside it,
              so a figure that contradicts the one above shows up here,
              while it is being typed. */}
          {form?.unit_minimums?.length > 0 && (
            <>
              <div className="field-split">
                {form.unit_minimums.map((u, i) => {
                  const least = Number(u.min_order_qty)
                  const comesTo =
                    least > 0 ? Number((least * Number(u.kg_per_unit)).toFixed(3)) : null
                  return (
                    <label className="field" key={u.unit_code}>
                      <span>Minimum in {u.label.toLowerCase()}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        inputMode="decimal"
                        value={u.min_order_qty ?? ''}
                        placeholder={`No minimum`}
                        onChange={(e) => {
                          const next = [...form.unit_minimums]
                          next[i] = { ...u, min_order_qty: e.target.value }
                          setForm({ ...form, unit_minimums: next })
                        }}
                      />
                      <span className="hint">
                        1 {u.label.toLowerCase().replace(/s$/, '')} = {Number(u.kg_per_unit)} kg
                        {comesTo != null && ` · this is ${comesTo} kg`}
                      </span>
                    </label>
                  )
                })}
              </div>
              <p className="hint">
                Used when a customer orders in that unit, instead of converting
                the figure above. Left blank, the minimum above is converted as
                before. Weights are set on Unit weights.
              </p>
            </>
          )}
          <p className="hint">
            {form?.id
              ? 'Renaming leaves the import key alone, so the next catalog import still recognises this category.'
              : 'The import key is taken from the name.'}
          </p>
        </form>
      </Drawer>

      {!data.length && <Empty icon="🏷️" title="No categories yet" />}

      {data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {SHOW_BULK_TRANSLATE && editable && (
                <th className="tick">
                  <input
                    type="checkbox"
                    aria-label="Select every category without Tamil"
                    checked={pending.length > 0 && chosen.length === pending.length}
                    onChange={(e) =>
                      e.target.checked ? selectAllPending() : setPicked({})
                    }
                  />
                </th>
              )}
              <th>Name</th>
              {SHOW_TAMIL_COLUMN && <th>Tamil</th>}
              {SHOW_IMPORT_KEY_COLUMN && <th>Import key</th>}
              <th>Minimum order</th>
              <th className="right">Products</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {data.map((category) => (
              <tr key={category.id}>
                {SHOW_BULK_TRANSLATE && editable && (
                  <td className="tick">
                    <input
                      type="checkbox"
                      aria-label={`Select ${category.name}`}
                      checked={Boolean(picked[category.id])}
                      onChange={(e) =>
                        setPicked((p) => ({ ...p, [category.id]: e.target.checked }))
                      }
                    />
                  </td>
                )}
                <td className="strong">
                  {category.name}
                  {category.name_ta && (
                    <span className="ta-line" lang="ta">{category.name_ta}</span>
                  )}
                </td>
                {SHOW_TAMIL_COLUMN && (
                  <td>
                    {category.name_ta ? (
                      <span className="ta-status done">✓ Translated</span>
                    ) : (
                      <span className="ta-status missing">Not translated</span>
                    )}
                  </td>
                )}
                {SHOW_IMPORT_KEY_COLUMN && <td className="mono muted">{category.slug}</td>}
                <td>
                  {category.min_order_qty ? (
                    `${Number(category.min_order_qty)} ${unitShort(category.base_unit)}`.trim()
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="right">
                  {category.product_count > 0 ? (
                    <Link to={`/products?category=${encodeURIComponent(category.name)}`}>
                      {category.product_count}
                    </Link>
                  ) : (
                    <span className="muted">0</span>
                  )}
                </td>
                {editable && (
                  <td className="right nowrap">
                    <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setNotice('')
                        setForm({
                          id: category.id,
                          name: category.name,
                          name_ta: category.name_ta ?? '',
                          min_order_qty: category.min_order_qty ? Number(category.min_order_qty) : '',
                          base_unit: category.base_unit,
                          // Editable field plus the weight it is read
                          // against, which this screen only displays.
                          unit_minimums: (category.unit_minimums ?? []).map((u) => ({
                            unit_code: u.unit_code,
                            label: u.label,
                            kg_per_unit: u.kg_per_unit,
                            min_order_qty:
                              u.min_order_qty == null ? '' : Number(u.min_order_qty),
                          })),
                        })
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={category.product_count > 0 || remove.isPending}
                      title={
                        category.product_count > 0
                          ? `${category.product_count} product(s) are in this category — move them first`
                          : undefined
                      }
                      onClick={() => {
                        setNotice('')
                        setConfirming(category)
                      }}
                    >
                      Delete
                    </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="hint">
        Categories also come from the catalog import, which matches on the import key.
      </p>
      <BulkTranslate
        open={bulk}
        entityType="category"
        label="categories"
        selectedIds={chosen.map((c) => c.id)}
        onClose={() => setBulk(false)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['category-management'] })
          setPicked({})
        }}
      />
      <Confirm
        open={Boolean(confirming)}
        title={`Delete the ${confirming?.name ?? ''} category?`}
        message="It holds no products, so nothing in the catalog moves."
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </div>
  )
}
